import {
  Badge,
  Box,
  Button,
  Divider,
  Form,
  PanelHeader,
  PanelSection,
  PanelTitle,
  TextField,
} from '@customerio/pluma-components/react';
import { useLoginFlow } from '../login-flow';
import { GoogleAuthButton } from '@/components/sso-sign-in-button';
import { getLastLoginMethod, LoginMethod } from '../utils/last-login-method';
import * as styles from '../index.css';

export function EmailStep() {
  const {
    email,
    emailError,
    isSubmitting,
    isAddingAccount,
    setEmail,
    setPassword,
    setTwoFactorAuthCode,
    submitEmail,
    googleOneClick,
  } = useLoginFlow();

  const lastLoginMethod = isAddingAccount ? null : getLastLoginMethod();
  const isEmailLastUsed = lastLoginMethod === LoginMethod.Email;
  const isGoogleLastUsed = lastLoginMethod === LoginMethod.Google;

  return (
    <>
      <PanelHeader>
        <PanelTitle level="1" data-test-login-signup-title>
          {isAddingAccount ? 'Log in to another Customer.io account' : 'Welcome back! Log in to Customer.io'}
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form
          id="login-email-form"
          onSubmit={(event) => {
            event.preventDefault();
            return submitEmail();
          }}
        >
          <Box mb="200" position="relative">
            {isEmailLastUsed ? (
              <Badge className={styles.badgeEmail} data-test-last-used-badge="email" color="neutral-outline">
                Last used
              </Badge>
            ) : null}
            <TextField
              data-test-login-form-input="email"
              autoComplete="username"
              name="email"
              type="text"
              label="Email"
              placeholder="you@company.com"
              value={email}
              onInput={(event) => setEmail(event.target.value)}
              error={emailError}
              autoFocus
              shouldAllowAutofill
            />
          </Box>

          {/* Off-screen inputs so password managers can autofill the password /
              one-time-code into the flow's shared state. */}
          {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- hidden password-manager autofill field needs a native input. */}
          <input
            aria-hidden="true"
            tabIndex={-1}
            className={styles.autofillHiddenInput}
            autoComplete="password"
            type="password"
            name="password"
            onInput={(event) =>
              setPassword(
                // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
                (event.target as HTMLInputElement).value,
              )
            }
          />
          {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- hidden password-manager autofill field needs a native input. */}
          <input
            aria-hidden="true"
            tabIndex={-1}
            className={styles.autofillHiddenInput}
            autoComplete="one-time-code"
            type="text"
            name="2fa_code"
            onInput={(event) =>
              setTwoFactorAuthCode(
                // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
                (event.target as HTMLInputElement).value,
              )
            }
          />

          <Box mt="300">
            <Button
              data-test-login-form-submit
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              w="full"
              isLoading={isSubmitting}
            >
              Log in to Customer.io
            </Button>
          </Box>

          <Divider label="or" mt="300" />

          <Box mt="300" position="relative">
            {isGoogleLastUsed ? (
              <Badge className={styles.badgeGoogle} data-test-last-used-badge="google" color="neutral-outline">
                Last used
              </Badge>
            ) : null}
            <GoogleAuthButton
              type="signup"
              isDisabled={isSubmitting}
              onClick={() => void googleOneClick()}
              data-testid="login-google-one-click"
            />
          </Box>
        </Form>
      </PanelSection>
    </>
  );
}
